import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// DoSoft brand colors
const DOSOFT_TEAL = '#6BA3B7';
const DOSOFT_GRAY = '#4A4A4A';

export const RestablecerPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No se proporcionó un token de recuperación válido');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/restablecer-password`, {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('¡Contraseña actualizada!');
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al restablecer la contraseña';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4" 
      style={{
        background: `linear-gradient(135deg, ${DOSOFT_TEAL} 0%, ${DOSOFT_GRAY} 50%, #2d3748 100%)`,
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite'
      }}>
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="absolute inset-0 bg-black/10"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_30a67381-0193-4972-adc8-4683263d2f57/artifacts/0sxu9ue3_Logo%20DoSoft.png" 
              alt="DoSoft" 
              className="h-16 w-auto"
            />
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${DOSOFT_TEAL}20` }}>
                <CheckCircle className="w-8 h-8" style={{ color: DOSOFT_TEAL }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: DOSOFT_GRAY }}>
                ¡Contraseña Actualizada!
              </h2>
              <p className="text-gray-600 mb-6">
                Tu contraseña ha sido restablecida exitosamente. Serás redirigido al inicio de sesión...
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                style={{ backgroundColor: DOSOFT_TEAL }}
              >
                Ir al Inicio de Sesión
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center mb-2" style={{ color: DOSOFT_GRAY }}>
                Restablecer Contraseña
              </h2>
              <p className="text-center text-gray-600 mb-8">
                Ingresa tu nueva contraseña
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-sm font-medium">Nueva Contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      placeholder="Mínimo 6 caracteres"
                      required
                      minLength={6}
                      disabled={!token}
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirmar Contraseña</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      placeholder="Repite la contraseña"
                      required
                      minLength={6}
                      disabled={!token}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold"
                  disabled={loading || !token}
                  style={{ backgroundColor: DOSOFT_TEAL }}
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Actualizando...
                    </>
                  ) : (
                    'Restablecer Contraseña'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-10 text-gray-600 hover:text-gray-800"
                  onClick={() => navigate('/login')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al Inicio de Sesión
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center mt-6 text-white text-sm drop-shadow-lg">
          © {new Date().getFullYear()} DoSoft
        </p>
      </div>
    </div>
  );
};
